import { useReducer, useCallback, useMemo, useEffect, useRef } from "react";
import * as Sd from "common/src/tft-static/Sd";
import * as Search from "../common/Search2";
import { traitRankCounts } from "common/src/tft-static/Sd";
import { deepEqual } from "fast-equals";
import { statsSet, lastPatch } from "common/src/Config";
import * as T from "common/src/bindings/Translation2";
import { use as useSet } from "common/src/data/TftSet.bs";
import * as Data from "common/src/data/Data.bs";

export type FU = {
  typ: "u";
  id: string;
  sid: string;
  starLevel: number;
  label: string;
};
export type FT = {
  typ: "t";
  id: string;
  sid: string;
  rank: number;
  label: string;
};

export type FI = {
  typ: "i";
  id: string;
  unitId: [string, number];
  label: string;
};
export type FA = {
  typ: "a";
  id: string;
  pick: number;
  label: string;
};
export type Filter = FU | FT | FI | FA;

export type Dp = {
  count: number;
  delta: number;
  place: number;
  top4: number;
  won: number;
};

export type ExplData = {
  units: Array<[string, Dp]>;
  star_units: Array<[string, number, Dp]>;
  traits: Array<[string, number, Dp]>;
  items: Array<[string, number, Dp]>;
  augments: Array<[number, Dp, Dp | null, Dp | null, Dp | null]>;
  base: {
    count: number;
    place: number;
    top4: number;
    won: number;
  };
};

const filtersToQuery2 = (filters: Array<Filter>): Array<string> => {
  const query: Array<string> = [];
  filters.forEach((f) => {
    switch (f.typ) {
      case "u":
        query.push(`u-${f.id}-${f.starLevel}`);
        break;
      case "t":
        query.push(`t-${f.id}-${f.rank}`);
        break;
      case "i":
        query.push(`i-${f.id}-${f.unitId[0]}-${f.unitId[1]}`);
        break;
      case "a":
        query.push(`a-${f.id}-${f.pick}`);
        break;
    }
  });

  return query;
};

export const explorerData = async (
  filters: Array<Filter>,
  patch: number,
  rankGroup: number,
): Promise<undefined | ExplData> => {
  const query = filtersToQuery2(filters).join("/");
  const response = await fetch(
    `https://d3.tft.tools/explorer-data/${patch}/${rankGroup}/${query}`,
  );
  if (response.status !== 200) {
    return undefined;
  }
  const json = await response.json();
  return json;
};

export type State = {
  filters: Array<Filter>;
  queryData: undefined | [Array<Filter>, ExplData | undefined];
  loading: boolean;
  patch: number;
  rankGroup: number;
  hidePlacements: boolean;
};

export type Action =
  | ["add_filter", Filter]
  | ["set_filter", [number, Filter | undefined]]
  | ["set_query_data", [Array<Filter>, ExplData | undefined]]
  | ["loading", undefined]
  | ["set_all_filters", Array<Filter>]
  | ["set_init_state", [Array<Filter>, number]]
  | ["set_patch", number]
  | ["set_rank_group", number];

const checkItems = (filters: Array<Filter>): Array<Filter> => {
  return filters.filterMap((f) => {
    if (f.typ === "i") {
      if (
        filters.some(
          (f2) =>
            f2.typ === "u" &&
            f2.id === f.unitId[0] &&
            f2.starLevel === f.unitId[1],
        )
      ) {
        return f;
      } else {
        const sameUnit = filters.find(
          (f2): f2 is FU => f2.typ === "u" && f2.id === f.unitId[0],
        );
        if (sameUnit) {
          return {
            ...f,
            unitId: [sameUnit.id, sameUnit.starLevel],
          };
        } else {
          const replacementUnit = filters.find(
            (f2): f2 is FU => f2.typ === "u",
          );
          if (replacementUnit && replacementUnit.typ === "u") {
            return {
              ...f,
              unitId: [replacementUnit.id, replacementUnit.starLevel],
            };
          }
        }
      }
    } else {
      return f;
    }
  });
};

const reduceAction = (state: State, [type, payload]: Action): State => {
  if (type === "set_filter") {
    let filters = state.filters.slice();
    if (payload[1] == null) {
      filters.splice(payload[0], 1);
    } else {
      const [idx, filter] = payload;
      if (filter.typ === "i" && filter.unitId[0] === "") {
        const unit = state.filters.find((f): f is FU => f.typ === "u");
        if (unit) {
          filter.unitId = [unit.id, unit.starLevel];
        } else {
          return state;
        }
      }
      filters[idx] = filter;
      filters = filters.filter((v) => v != null);
    }
    filters = checkItems(filters);
    return {
      ...state,
      filters,
    };
  } else if (type === "add_filter") {
    const filter = payload;
    if (filter.typ === "i" && filter.unitId[0] === "") {
      const unit = state.filters.find((f): f is FU => f.typ === "u");
      if (unit) {
        filter.unitId = [unit.id, unit.starLevel];
      } else {
        return state;
      }
    }
    return {
      ...state,
      filters: state.filters.concat([payload]),
    };
  } else if (type === "set_query_data") {
    // const hidePlacements =
    //   payload[1] != null && payload[0].some((f) => f.typ === "a");
    const hidePlacements = false;
    return {
      ...state,
      queryData: payload,
      loading: false,
      hidePlacements,
    };
  } else if (type === "loading") {
    return {
      ...state,
      loading: true,
    };
  } else if (type === "set_init_state") {
    return {
      ...state,
      filters: payload[0],
      rankGroup: payload[1],
    };
  } else if (type === "set_all_filters") {
    return {
      ...state,
      filters: payload,
    };
  } else if (type === "set_rank_group") {
    return {
      ...state,
      rankGroup: payload,
    };
  } else if (type === "set_patch") {
    return {
      ...state,
      patch: payload,
    };
  }

  return state;
};

let allUnitFilters_: null | Array<FU> = null;
let allTraitFilters_: null | Array<FT> = null;
let allItemFilters_: null | Array<FI> = null;
let allAugmentFilters_: null | Array<FA> = null;
export const allUnitFilters = (): Array<FU> => {
  if (allUnitFilters_ !== null) {
    return allUnitFilters_;
  } else {
    const allUnits: Array<FU> = Sd.allUnits4(statsSet).map((id) => {
      return {
        typ: "u",
        id,
        sid: id,
        starLevel: 0,
        label: T.units(id),
      };
    });
    allUnitFilters_ = allUnits;
    return allUnits;
  }
};
export const allTraitFilters = (): Array<FT> => {
  if (allTraitFilters_ !== null) {
    return allTraitFilters_;
  } else {
    const allTraits: Array<FT> = Sd.allTraits2(statsSet)
      .map((id) => {
        return (traitRankCounts(id, statsSet) || []).map(
          (count, idx): FT => ({
            typ: "t",
            id,
            sid: id,
            rank: idx + 1,
            label: count + " " + T.traits(id),
          }),
        );
      })
      .flat();
    allTraitFilters_ = allTraits;
    return allTraitFilters_;
  }
};
export const allItemFilters = (): Array<FI> => {
  if (allItemFilters_ !== null) {
    return allItemFilters_;
  } else {
    const allItems: Array<FI> = Object.entries(Data.items(statsSet))
      .filter(
        ([sid, info]) =>
          info.id > 8 && !sid.includes("Chempunk") && !sid.endsWith("_HR"),
      )
      .filter(([_, info]) => {
        return info?.tags?.some((tag) => {
          switch (tag) {
            case "Artifact":
            case "Craftable":
            case "CraftableEmblem":
            case "Radiant":
            case "ElusiveEmblem":
            case "Support":
            case "Other":
              return true;
            default:
              return false;
          }
        });
      })
      .map(([id, _v]) => ({
        typ: "i",
        id: id,
        unitId: ["", 0],
        label: T.items(id),
      }));
    allItemFilters_ = allItems;
    return allItemFilters_;
  }
};
export const allAugmentFilters = (): Array<FA> => {
  if (allAugmentFilters_ !== null) {
    return allAugmentFilters_;
  } else {
    const allAugments: Array<FA> = Object.entries(Data.augments(statsSet))
      .filter(([_id, v]) => v.typ != "hr")
      .map(([id, v]) => {
        let altLabel = undefined;
        return {
          typ: "a",
          id: id,
          pick: 0,
          label: T.augments(id),
          altLabel,
        };
      });
    allAugmentFilters_ = allAugments;
    return allAugments;
  }
};

export const useExState = (): [
  State,
  (action: Action) => void,
  Array<Filter>,
  (search: string) => Array<Filter>,
] => {
  const set = useSet();
  const [state, dispatch] = useReducer(reduceAction, {
    filters: [],
    queryData: undefined,
    loading: false,
    rankGroup: 1,
    patch: lastPatch,
    hidePlacements: false,
  });

  const fullData = useMemo(() => {
    const allUnits: Array<FU> = allUnitFilters();
    const allTraits: Array<FT> = allTraitFilters();
    const allItems: Array<FI> = allItemFilters();
    // const allAugments: Array<FA> = allAugmentFilters();

    const fullData = [...allUnits, ...allTraits, ...allItems];

    return fullData;
  }, []);

  const getMatches = useCallback((search: string) => {
    const matches: Array<Filter> = [];

    const allUnits: Array<FU> = allUnitFilters();
    let unitMatches = Search.searchUnitsExact(search, set);
    allUnits.forEach((v) => {
      if (unitMatches.has(v.id)) {
        matches.push(v);
      }
    });

    const allTraits: Array<FT> = allTraitFilters();
    let traitMatches = Search.searchRankTraits(search, set);
    allTraits.forEach((v) => {
      if (traitMatches.has(`${v.id}-${v.rank}`)) {
        matches.push(v);
      }
    });

    const allItems: Array<FI> = allItemFilters();
    let itemMatches = Search.searchItems(search, set);
    allItems.forEach((v) => {
      if (itemMatches.has(v.id)) {
        matches.push(v);
      }
    });

    // const allAugments: Array<FA> = allAugmentFilters();
    // let augMatches = Search.searchAugments(search, set);
    // allAugments.forEach((v) => {
    //   if (augMatches.has(v.id)) {
    //     matches.push(v);
    //   }
    // });

    return matches;
  }, []);

  const prevQuery = useRef([state.filters, state.patch, state.rankGroup]);

  useEffect(() => {
    if (state.filters.length > 0) {
      if (
        !deepEqual(state.filters, prevQuery.current[0]) ||
        state.patch !== prevQuery.current[1] ||
        state.rankGroup !== prevQuery.current[2]
      ) {
        dispatch(["loading", undefined]);

        explorerData(state.filters, state.patch, state.rankGroup).then(
          (data) => {
            if (data) {
              dispatch(["set_query_data", [state.filters, data]]);
            } else {
              dispatch(["set_query_data", [state.filters, undefined]]);
            }
          },
        );
        prevQuery.current = [state.filters, state.patch, state.rankGroup];
      }
    } else {
      dispatch(["set_query_data", [[], undefined]]);
    }
  }, [state.filters, state.patch, state.rankGroup]);

  return [state, dispatch, fullData, getMatches];
};
